import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Field, inputClass } from '../../components/ui/Field';
import { attendanceSummary } from '../../utils/attendance';
import { eventTitle, formatDate, functionLabel, functionGroups, fullName, memberTypeClass, memberTypeLabel, memberTypeOrder, voiceClass, voiceLabel, voiceOrder } from '../../utils/labels';
import type { ChoirFunction, MemberType, Voice } from '../../types';
export function AdminMemberDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    members,
    settings,
    updateMember,
    fees,
    setFeePaid,
    past,
    attendance,
    isExpected,
    pushToast
  } = useApp();
  const member = members.find(m => m.id === id);
  const [urejanje, setUrejanje] = useState(false);
  const [osnutek, setOsnutek] = useState(member);
  const zgodovina = useMemo(() => {
    if (!member) return [];
    return past.filter(e => e.steje && isExpected(e, member.id)).slice(0, 6).map(e => ({
      e,
      mark: attendance[e.id]?.[member.id] ?? 'prisoten'
    }));
  }, [past, attendance, isExpected, member]);
  if (!member || !osnutek) {
    return <Card className="p-10 text-center" data-id="element-1120" data-mp="2jqhqg4gmiykc">
        <p className="text-sm font-medium text-ink" data-id="element-1121" data-mp="2jp6q6eme08xz">Član ne obstaja.</p>
        <Link to="/admin/clani" className="mt-2 inline-block text-[13px] font-medium text-green-600" data-id="element-1122" data-mp="2ery78btf00vo">
          Nazaj na seznam
        </Link>
      </Card>;
  }
  const s = attendanceSummary(member, settings);
  const clanarine = fees.filter(f => f.memberId === member.id).sort((a, b) => a.leto - b.leto);
  function toggleFunkcija(f: ChoirFunction) {
    if (!osnutek) return;
    setOsnutek({
      ...osnutek,
      functions: osnutek.functions.includes(f) ? osnutek.functions.filter(x => x !== f) : [...osnutek.functions, f]
    });
  }
  return <div className="mx-auto max-w-5xl space-y-4" data-id="element-1123" data-mp="10rtnzij14s5g">
      <button onClick={() => navigate('/admin/clani')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-1124" data-mp="10qikljthxmev">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1125" data-mp="quyqn2yhsxsr" />
        Nazaj na člane
      </button>

      <Card className="p-5" data-id="element-1126" data-mp="cv61hc2m6pmw">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1127" data-mp="cvyz9njli4sw">
          <div className="flex items-center gap-4" data-id="element-1128" data-mp="hthc3p0qh466">
            <Avatar member={member} size="lg" data-id="element-1129" data-mp="2785gb2z67ft4" />
            <div data-id="element-1130" data-mp="278f88w3rzx1g">
              <h1 className="text-xl font-semibold text-ink" data-id="element-1131" data-mp="275t1gyopllka">{fullName(member)}</h1>
              <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-1132" data-mp="2c677ycanwng7">Član/ica od {formatDate(member.clanOd)}</p>
              <div className="mt-2 flex flex-wrap gap-3 text-[13px] text-ink-muted" data-id="element-1133" data-mp="hiuravvy9qj">
                <span className="flex items-center gap-1.5" data-id="element-1134" data-mp="3rrhlfv37zcrb">
                  <MailIcon className="h-3.5 w-3.5" data-id="element-1135" data-mp="a9ol6g6hoj3m" />
                  {member.email}
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1136" data-mp="5hor34d3okey">
                  <PhoneIcon className="h-3.5 w-3.5" data-id="element-1137" data-mp="ja4mpk3o9g5u" />
                  {member.telefon}
                </span>
              </div>
            </div>
          </div>
          {urejanje ? <div className="flex gap-2" data-id="element-1138" data-mp="ede68vnygijw">
              <Button variant="secondary" onClick={() => {
            setOsnutek(member);
            setUrejanje(false);
          }} data-id="element-1139" data-mp="t8kfdrickxhg">
                Prekliči
              </Button>
              <Button onClick={() => {
            updateMember(osnutek);
            setUrejanje(false);
            pushToast('Podatki člana so shranjeni.');
          }} data-id="element-1140" data-mp="2v1rt4wibb718">
                Shrani
              </Button>
            </div> : <Button variant="secondary" onClick={() => setUrejanje(true)} data-id="element-1141" data-mp="1m4jvijygvejl">
              Uredi podatke
            </Button>}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]" data-id="element-1142" data-mp="1v23z3zjsxmxd">
        <Card className="p-5" data-id="element-1143" data-mp="ydeh8986cube">
          <h2 className="text-base font-semibold text-ink" data-id="element-1144" data-mp="ye7hcrcmolkk">Članstvo</h2>
          {urejanje ? <div className="mt-4 space-y-4" data-id="element-1145" data-mp="13dajo4184dou">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-1146" data-mp="139v1utg60qjq">
                <Field label="Tip članstva" htmlFor="tip" data-id="element-1147" data-mp="17d18413a2hq7">
                  <select id="tip" value={osnutek.type} onChange={e => setOsnutek({
                ...osnutek,
                type: e.target.value as MemberType
              })} className={inputClass} data-id="element-1148" data-mp="1c4aw6x96hczz">
                    {memberTypeOrder.map((t: MemberType) => <option key={t} value={t} data-id="element-1149" data-mp="c3o5muf7m1oe">
                        {memberTypeLabel[t]}
                      </option>)}
                  </select>
                </Field>
                <Field label="Glasovna skupina" htmlFor="glas" data-id="element-1150" data-mp="339fnhravyek">
                  <select id="glas" value={osnutek.voice} onChange={e => setOsnutek({
                ...osnutek,
                voice: e.target.value as Voice
              })} className={inputClass} data-id="element-1151" data-mp="3uac8edmsecqw">
                    {voiceOrder.map(v => <option key={v} value={v} data-id="element-1152" data-mp="3fl6dh1uzf8ot">
                        {voiceLabel[v]}
                      </option>)}
                  </select>
                </Field>
              </div>
              <div data-id="element-1153" data-mp="36lax8imkknhx">
                <p className="mb-1 text-[13px] font-medium text-ink" data-id="element-1154" data-mp="36jq32u4636kl">Funkcije v zboru</p>
                <p className="mb-3 text-xs text-ink-muted" data-id="element-1155" data-mp="31let8jdvgw2d">
                  Funkcije so neodvisne od tipa članstva in glasovne skupine.
                </p>
                <div className="space-y-3" data-id="element-1156" data-mp="2s0f7w6253coa">
                  {functionGroups.map(g => <div key={g.label} data-id="element-1157" data-mp="2wv2pdkyq76td">
                      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1158" data-mp="2i0obtzxxkpm3">
                        {g.label}
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1159" data-mp="3aok66y0125fp">
                        {g.funkcije.map(f => {
                    const izbrana = osnutek.functions.includes(f);
                    return <button key={f} type="button" onClick={() => toggleFunkcija(f)} aria-pressed={izbrana} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${izbrana ? 'border-green-300 bg-green-50 text-green-700' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-1160" data-mp="30zaf9lgx6st6">
                              {functionLabel[f]}
                            </button>;
                  })}
                      </div>
                    </div>)}
                </div>
              </div>
              <Field label="Interna opomba" htmlFor="opomba" data-id="element-1161" data-mp="z1qdj9sm1htl">
                <textarea id="opomba" rows={3} value={osnutek.opomba ?? ''} onChange={e => setOsnutek({
              ...osnutek,
              opomba: e.target.value
            })} className={inputClass} data-id="element-1162" data-mp="z3b6ww36uupv" />
              </Field>
            </div> : <div className="mt-4 grid gap-4 sm:grid-cols-3" data-id="element-1163" data-mp="2kf2xkxqy00fq">
              <div data-id="element-1164" data-mp="2kgmfstw5slk5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1165" data-mp="hrwbe9r438tj">Tip članstva</p>
                <div className="mt-1.5" data-id="element-1166" data-mp="mqplds76whoy">
                  <Badge className={memberTypeClass[member.type]} data-id="element-1167" data-mp="mrh8eemq173m">{memberTypeLabel[member.type]}</Badge>
                </div>
              </div>
              <div data-id="element-1168" data-mp="wgsfjvq1s1ov">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1169" data-mp="whagv6rkh63n">Glas</p>
                <div className="mt-1.5" data-id="element-1170" data-mp="10j5k4gt25tk2">
                  <Badge className={voiceClass[member.voice]} data-id="element-1171" data-mp="10i4a8fns3d3x">{voiceLabel[member.voice]}</Badge>
                </div>
              </div>
              <div data-id="element-1172" data-mp="1agtfn8yhn53a">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1173" data-mp="1aga9sqm4475m">Funkcije</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1174" data-mp="1felk12gbkjod">
                  {member.functions.map(f => <Badge key={f} data-id="element-1175" data-mp="3c9hmdsaccant">{functionLabel[f]}</Badge>)}
                </div>
              </div>
              {member.opomba && <p className="sm:col-span-3 rounded-lg bg-canvas px-3 py-2.5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1176" data-mp="1ybn5094ic8yj">
                  {member.opomba}
                </p>}
            </div>}
        </Card>

        <Card className="p-5" data-id="element-1177" data-mp="1pkd23r7lj0h6">
          <div className="flex items-start justify-between gap-3" data-id="element-1178" data-mp="1phh462vr0bsq">
            <h2 className="text-base font-semibold text-ink" data-id="element-1179" data-mp="1klrwtddhlqlv">Prisotnost</h2>
            {member.type === 'aktivni' && <StatusBadge status={s.status} odstotek={s.odstotek} data-id="element-1180" data-mp="2ha7pvjft2i3q" />}
          </div>
          <p className="mt-2 text-sm text-ink" data-id="element-1181" data-mp="2da7de4c9ogzb">
            {s.prisoten} od {s.pricakovan} dogodkov · sezona 2026/27
          </p>
          <ul className="mt-3 divide-y divide-line" data-id="element-1182" data-mp="2888aswocpo3f">
            {zgodovina.map(({
            e,
            mark
          }) => <li key={e.id} className="flex items-center gap-3 py-2.5" data-id="element-1183" data-mp="23d2o3vn4k5ea">
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${mark === 'prisoten' ? 'bg-green-50 text-green-600' : 'bg-rose-50 text-rose-600'}`} data-id="element-1184" data-mp="wvp9fakip8tl">
                  {mark === 'prisoten' ? <CheckIcon className="h-3.5 w-3.5" data-id="element-1185" data-mp="311br5n2oq19o" /> : <XIcon className="h-3.5 w-3.5" data-id="element-1186" data-mp="30y61a5m8evcw" />}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-1187" data-mp="3fpyg8gi467e3">{eventTitle(e)}</span>
                <span className="shrink-0 text-[12px] text-ink-muted" data-id="element-1188" data-mp="3axol8m256h4y">{formatDate(e.datum)}</span>
              </li>)}
            {zgodovina.length === 0 && <li className="py-4 text-[13px] text-ink-muted" data-id="element-1189" data-mp="3jrjp7qrj21j0">Ni zabeleženih dogodkov.</li>}
          </ul>
        </Card>
      </div>

      <Card className="p-5" data-id="element-1190" data-mp="2hvcite1y9yg">
        <h2 className="text-base font-semibold text-ink" data-id="element-1191" data-mp="3toy43lxsyi97">Članarine</h2>
        <ul className="mt-3 divide-y divide-line" data-id="element-1192" data-mp="3tppt2dww9hqi">
          {clanarine.map(f => <li key={f.leto} className="flex items-center gap-3 py-3 text-sm" data-id="element-1193" data-mp="24ajihai2zbhx">
              <span className="w-16 font-medium text-ink" data-id="element-1194" data-mp="1zbyhh8v08nsf">{f.leto}</span>
              <span className="flex-1 text-ink-muted" data-id="element-1195" data-mp="escn4vdqinpl">
                {f.znesek} €{f.datumPlacila ? ` · plačano ${formatDate(f.datumPlacila)}` : ''}
              </span>
              {f.placano ? <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-1196" data-mp="53kzv0jmcrl0">
                  Poravnano
                </Badge> : <Button size="sm" variant="secondary" onClick={() => setFeePaid(member.id, f.leto, true)} data-id="element-1197" data-mp="9ztbw52is4ny">
                  Označi kot plačano
                </Button>}
            </li>)}
        </ul>
      </Card>
    </div>;
}